import styled from '@emotion/styled'
import { DatePicker, Input, Select } from 'antd'
import { QuestionCircleOutlined } from '@ant-design/icons'
import { useFormikContext } from 'formik'
import { useEffect, useState } from 'react'
import dayjs, { Dayjs } from 'dayjs'
import { useSelector } from 'src/hooks/useRedux'
import type { WizardFormValues } from '@sportninja/common/features/competition/wizard'
import {
  getSports,
  getAllGenericSportProperties,
} from '@sportninja/common/selectors/types'
import Text from 'src/components/ts-components/Text'
import { paragraphSmall } from 'src/components/css'
import { competitionWord } from '@sportninja/common/features/competition/wizard'
import colors from '@sportninja/common/constants/appColors'

const STATS_HELP_URL =
  'https://support.sportninja.com/scoring-2-versions-basic-vs-full-stats'

const GroupHeader = ({ label }: { label: string }) => (
  <GroupHeaderRow>
    <GroupHeaderLabel>{label}</GroupHeaderLabel>
    <HelpIcon
      onClick={(e) => {
        e.stopPropagation()
        window.open(STATS_HELP_URL, '_blank')
      }}
    />
  </GroupHeaderRow>
)

type Step2Props = {
  /** Org's contact email — pre-fills the Email input when first set. */
  defaultEmail?: string | null
  /** Org's contact website — pre-fills the Website input when first set. */
  defaultWebsite?: string | null
}

const Step2Info = ({ defaultEmail, defaultWebsite }: Step2Props) => {
  const { values, errors, touched, setFieldValue, setFieldTouched } =
    useFormikContext<WizardFormValues>()

  // Pre-fill Email + Website from the parent org once, only when the fields
  // are still empty so a user who has typed something keeps their input.
  useEffect(() => {
    if (!values.info.email && defaultEmail) {
      setFieldValue('info.email', defaultEmail)
    }
    if (!values.info.website && defaultWebsite) {
      setFieldValue('info.website', defaultWebsite)
    }
    // Intentionally only react to defaults arriving — not to the user editing
    // the field afterward.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultEmail, defaultWebsite])

  const [endDateOpen, setEndDateOpen] = useState(false)
  // SN-8028: own the open panel month locally so re-renders (Formik context
  // churn on focus/hover) can't reset a controlled DatePicker's panel back to
  // `value`'s month and swallow an in-progress year/month selection. See the
  // matching START/END handlers in Step1CloneForm for the full rationale.
  const [startPanel, setStartPanel] = useState<Dayjs | null>(null)
  const [endPanel, setEndPanel] = useState<Dayjs | null>(null)

  const infoErrors = (typeof errors.info === 'object' && errors.info) || {}
  const infoTouched = (typeof touched.info === 'object' && touched.info) || {}
  const fieldError = (key: keyof typeof values.info): string | undefined => {
    const isTouched = (infoTouched as Record<string, unknown>)[key as string]
    if (!isTouched) {
      return undefined
    }
    const msg = (infoErrors as Record<string, unknown>)[key as string]
    return typeof msg === 'string' ? msg : undefined
  }
  const sports = useSelector((state: any) => Object.values(getSports(state)))
  const genericSports = useSelector((state: any) =>
    getAllGenericSportProperties(state)
  )

  const genericId = (
    sports.find((s: any) => s?.name?.toLowerCase().includes('generic')) as
      | { id?: string }
      | undefined
  )?.id

  const fullStatsOptions = sports
    .map((s: any) => ({
      value: s.id,
      label: s.name_full,
      isGeneric: false,
    }))
    .filter((o: any) => !o.label.toLowerCase().includes('generic'))
    .sort((a: any, b: any) => a.label.localeCompare(b.label))

  const basicStatsOptions = (genericSports ?? [])
    .map((s: any) => ({
      value: s.id,
      label: s.name,
      isGeneric: true,
    }))
    .sort((a: any, b: any) => a.label.localeCompare(b.label))

  const sportOptions = [
    {
      label: <GroupHeader label='Full Stats' />,
      title: 'fullStats',
      options: fullStatsOptions,
    },
    {
      label: <GroupHeader label='Basic Stats' />,
      title: 'basicStats',
      options: basicStatsOptions,
    },
  ]

  const selectedValue =
    values.info.genericSportId ?? values.info.sportId ?? undefined

  const handleSportChange = (value: string | undefined) => {
    if (!value) {
      setFieldValue('info.sportId', null)
      setFieldValue('info.sportType', null)
      setFieldValue('info.genericSportId', null)
      return
    }
    const generic = basicStatsOptions.find((o: any) => o.value === value)
    if (generic) {
      // For generic sports, send the chosen sport's own name (e.g. 'football')
      // — not the parent 'generic' placeholder.
      setFieldValue('info.sportId', genericId ?? null)
      setFieldValue('info.sportType', generic.label ?? null)
      setFieldValue('info.genericSportId', value)
    } else {
      const sportName = (
        sports.find((s: any) => s?.id === value) as
          | { name?: string }
          | undefined
      )?.name
      setFieldValue('info.sportId', value)
      setFieldValue('info.sportType', sportName ?? null)
      setFieldValue('info.genericSportId', null)
    }
  }

  const filterOption = (input: string, option: any) => {
    if (Array.isArray(option?.options)) {
      return false
    }
    return (option?.label ?? '')
      .toString()
      .toLowerCase()
      .includes(input.toLowerCase())
  }

  return (
    <Container>
      <Header>
        <Text variant='heading3' weight='semiBold' color={colors.WHITE}>
          General Info
        </Text>
        <Text variant='paragraphMedium' color={colors.NEUTRAL_50}>
          Tell us the basics about your {competitionWord({ capitalize: false })}
          .
        </Text>
      </Header>

      <Form>
        <TwoColumnGrid>
          <Field>
            <FieldLabel>
              <Text
                variant='paragraphSmall'
                weight='medium'
                color={colors.WHITE}
              >
                {competitionWord()} Name
              </Text>
            </FieldLabel>
            <Input
              value={values.info.name}
              onChange={(e) => setFieldValue('info.name', e.target.value)}
              placeholder='Summer Showdown 2026'
            />
          </Field>
          <Field>
            <FieldLabel>
              <Text
                variant='paragraphSmall'
                weight='medium'
                color={colors.WHITE}
              >
                Sport
              </Text>
            </FieldLabel>
            <Select
              style={{ width: '100%' }}
              options={sportOptions}
              value={selectedValue}
              onChange={handleSportChange}
              placeholder='Select Sport'
              showSearch
              filterOption={filterOption}
              allowClear
            />
          </Field>
        </TwoColumnGrid>

        <TwoColumnGrid>
          <Field>
            <FieldLabel>
              <Text
                variant='paragraphSmall'
                weight='medium'
                color={colors.WHITE}
              >
                Start Date
              </Text>
            </FieldLabel>
            <DatePicker
              style={{ width: '100%' }}
              value={values.info.startsAt ? dayjs(values.info.startsAt) : null}
              pickerValue={startPanel ?? undefined}
              onOpenChange={(open) => {
                if (open) {
                  setStartPanel(
                    values.info.startsAt ? dayjs(values.info.startsAt) : dayjs()
                  )
                }
              }}
              onPickerValueChange={(v, info) => {
                // Only follow real panel navigation; ignore antd's `reset`
                // re-derivation on re-render so an in-progress year/month jump
                // isn't snapped back (SN-8028).
                if (info?.source === 'panel') {
                  setStartPanel(v)
                }
              }}
              onChange={(d: Dayjs | null) => {
                setFieldValue(
                  'info.startsAt',
                  d ? d.format('YYYY-MM-DD') : null
                )
                // Clear any previously-picked end date and pop the End picker
                // open so the user keeps flowing left-to-right.
                setFieldValue('info.endsAt', null)
                if (d) {
                  setEndDateOpen(true)
                }
              }}
              format='DD/MM/YYYY'
            />
          </Field>
          <Field>
            <FieldLabel>
              <Text
                variant='paragraphSmall'
                weight='medium'
                color={colors.WHITE}
              >
                End Date
              </Text>
            </FieldLabel>
            <DatePicker
              style={{ width: '100%' }}
              open={endDateOpen}
              value={values.info.endsAt ? dayjs(values.info.endsAt) : null}
              pickerValue={endPanel ?? undefined}
              onOpenChange={(open) => {
                setEndDateOpen(open)
                // Seed the panel on open: existing end date, else the start
                // month, else today. Local state then owns the panel month so
                // re-renders can't reset it mid-navigation (SN-8028).
                if (open) {
                  setEndPanel(
                    values.info.endsAt
                      ? dayjs(values.info.endsAt)
                      : values.info.startsAt
                      ? dayjs(values.info.startsAt)
                      : dayjs()
                  )
                }
              }}
              onPickerValueChange={(v, info) => {
                if (info?.source === 'panel') {
                  setEndPanel(v)
                }
              }}
              onChange={(d: Dayjs | null) =>
                setFieldValue('info.endsAt', d ? d.format('YYYY-MM-DD') : null)
              }
              format='DD/MM/YYYY'
              disabledDate={(current) =>
                !!values.info.startsAt &&
                current.isBefore(dayjs(values.info.startsAt), 'day')
              }
            />
          </Field>
        </TwoColumnGrid>

        <TwoColumnGrid>
          <Field>
            <FieldLabel>
              <Text
                variant='paragraphSmall'
                weight='medium'
                color={colors.WHITE}
              >
                Email
              </Text>
            </FieldLabel>
            <Input
              type='email'
              value={values.info.email}
              onChange={(e) => setFieldValue('info.email', e.target.value)}
              onBlur={() => {
                setFieldTouched('info.email', true)
                // surface the "at least one" message that lives on `website`
                setFieldTouched('info.website', true)
              }}
              placeholder='competition@email.com'
            />
            {fieldError('email') ? (
              <Text variant='paragraphXSmall' color={colors.ERROR_200}>
                {fieldError('email')}
              </Text>
            ) : null}
          </Field>
          <Field>
            <FieldLabel>
              <Text
                variant='paragraphSmall'
                weight='medium'
                color={colors.WHITE}
              >
                Website
              </Text>
            </FieldLabel>
            <Input
              value={values.info.website}
              onChange={(e) => setFieldValue('info.website', e.target.value)}
              onBlur={() => {
                setFieldTouched('info.website', true)
                setFieldTouched('info.email', true)
              }}
              placeholder='www.competition.com'
            />
            {fieldError('website') ? (
              <Text variant='paragraphXSmall' color={colors.ERROR_200}>
                {fieldError('website')}
              </Text>
            ) : null}
          </Field>
        </TwoColumnGrid>
      </Form>
    </Container>
  )
}

export default Step2Info

// ---- styled ----
const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  width: 100%;

  /* Figma input styling — Ant Input, Select, DatePicker */
  .ant-input,
  .ant-picker {
    ${paragraphSmall};
    background-color: ${colors.SECONDARY_300} !important;
    border: 1px solid ${colors.SECONDARY_200} !important;
    border-radius: 16px !important;
    padding: 16px !important;
    height: auto !important;
    color: ${colors.WHITE} !important;
  }
  .ant-select-single {
    height: 54px !important;
  }
  .ant-select .ant-select-selector {
    ${paragraphSmall};
    background-color: ${colors.SECONDARY_300} !important;
    border: 1px solid ${colors.SECONDARY_200} !important;
    border-radius: 16px !important;
    height: 54px !important;
    color: ${colors.WHITE} !important;
  }
  .ant-select-single .ant-select-selector .ant-select-selection-item,
  .ant-select-single .ant-select-selector .ant-select-selection-placeholder {
    line-height: 52px !important;
    padding-inline-start: 0 !important;
  }
  /* hide blinking text cursor of the search input once a value is selected */
  .ant-select-single .ant-select-selector .ant-select-selection-search,
  .ant-select-single .ant-select-selector .ant-select-selection-search-input {
    inset-inline-start: 16px !important;
    caret-color: transparent !important;
  }
  .ant-select-single .ant-select-selector .ant-select-selection-placeholder {
    color: ${colors.NEUTRAL_100} !important;
  }
  .ant-select .ant-select-arrow {
    inset-inline-end: 16px !important;
    top: 50% !important;
    bottom: auto !important;
    margin: 0 !important;
    height: 12px !important;
    transform: translateY(-50%) !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
  }
  .ant-picker-input > input {
    ${paragraphSmall};
    color: ${colors.WHITE} !important;
  }
  .ant-picker-input > input::placeholder {
    color: ${colors.NEUTRAL_100} !important;
    opacity: 1 !important;
  }
  .ant-input::placeholder {
    color: ${colors.NEUTRAL_100} !important;
    opacity: 1 !important;
  }
  .ant-select-arrow,
  .ant-picker-suffix {
    color: ${colors.WHITE} !important;
  }
`

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  text-align: center;
`

const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  max-width: 720px;
`

const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const FieldLabel = styled.div`
  /* spacing handled by parent Field gap */
`

const GroupHeaderRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`

const GroupHeaderLabel = styled.span`
  color: ${colors.PRIMARY};
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
`

const HelpIcon = styled(QuestionCircleOutlined)`
  font-size: 16px;
  color: ${colors.PRIMARY};
  cursor: pointer;
`
